import { ChartOptions, ChartType, TooltipItem } from "chart.js";
import { t } from "i18next";
import { StatisticsDataItemProps } from "modules/financial";
import moment from "moment";

import {
  CHART_BODY_FONT,
  CHART_TITLE_FONT,
  CHART_X_TICKS_CONFIG,
  CHART_Y_TICKS_CONFIG,
  ChartRangeType,
  STATISTIC_BARS_WIDTH
} from "../constants";
import {
  getChartBarColumnsBottomLabels,
  getChartBarLabel,
  getPreparedBarsData
} from "../helpers";

interface useChartProps {
  barsData: StatisticsDataItemProps[];
  dateRange: [Date, Date];
  activeRangeType: ChartRangeType;
  isLoadingData?: boolean;
}

const useChart = ({
  dateRange,
  activeRangeType,
  barsData,
  isLoadingData
}: useChartProps) => {
  const preparedBarsData = getPreparedBarsData({
    dateRange,
    activeRangeType,
    barsData
  });

  const getIsAllMoneyCollectedByIndex = (
    index: number
  ): boolean | undefined => {
    const labels = getChartBarColumnsBottomLabels(dateRange, activeRangeType);
    const label = labels[index];
    if (!label) return undefined;

    const found = barsData.find((item) => {
      const itemDate = moment(item.date).format("YYYY-MM-DD");
      const labelDate = moment(dateRange[0])
        .add(index, activeRangeType === ChartRangeType.Year ? "months" : "days")
        .format("YYYY-MM-DD");
      return itemDate === labelDate;
    });

    return found?.isAllMoneyCollected;
  };

  const maxValue = Math.ceil(Math.max(...preparedBarsData) / 200) * 200;
  const minValue = Math.ceil(Math.min(...preparedBarsData) / 200) * 200;

  const state = {
    labels: getChartBarColumnsBottomLabels(dateRange, activeRangeType),
    datasets: [
      {
        backgroundColor: isLoadingData ? "#f0f0f0" : "#FFEBF1",
        hoverBackgroundColor: "#FB3973",
        borderRadius: 4,
        borderSkipped: false,
        barThickness: STATISTIC_BARS_WIDTH[activeRangeType],
        data: preparedBarsData
      }
    ]
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        xAlign: "center",
        yAlign: "bottom",
        backgroundColor: "#1B1D21",
        displayColors: false,
        callbacks: {
          title: ([titleProps]: Array<TooltipItem<ChartType>>) =>
            getChartBarLabel(titleProps.label, dateRange[0], activeRangeType),
          label: (tooltipItem: TooltipItem<ChartType>) =>
            `${tooltipItem.formattedValue}`,
          footer: (tooltipItems: Array<TooltipItem<ChartType>>) => {
            const index = tooltipItems[0]?.dataIndex;
            if (index === undefined) return [];
            const isAllMoneyCollected = getIsAllMoneyCollectedByIndex(index);
            if (isAllMoneyCollected === false) {
              return [
                t("Not all money was collected"),
                t("by the driver for this period.")
              ];
            }
            return [];
          }
        },
        titleFont: CHART_TITLE_FONT,
        bodyFont: CHART_BODY_FONT,
        footerFont: {
          family: "'Eudoxus Sans', sans-serif",
          size: 10,
          weight: 400
        },
        titleColor: "#ffffff",
        footerColor: "#B8BCC4",
        titleAlign: "center",
        footerAlign: "center",
        bodyAlign: "center",
        titleMarginBottom: 2,
        padding: { top: 5, right: 12, bottom: 5, left: 12 }
      },
      noData: isLoadingData
        ? null
        : { customMessage: t(`No data for this ${activeRangeType}`) }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          offset: true,
          display: false
        },
        ticks: CHART_X_TICKS_CONFIG,
        border: { display: false }
      },
      y: {
        beginAtZero: true,
        min: minValue >= 0 ? 0 : minValue,
        max: maxValue && maxValue > 1000 ? maxValue : 1000,
        ticks: CHART_Y_TICKS_CONFIG,
        grid: { display: false },
        border: { display: false }
      }
    }
  };

  return { state, options };
};

export default useChart;
